/**
 * Per-instance symbol + auto-switch state for the broker widgets.
 *
 * Each Broker Ticket / Broker Ladder on a dashboard owns its own loaded asset
 * and auto-switch mode, persisted under its `widgetCompositeId` — so one can
 * follow the headline feed while another stays pinned to a symbol. Widgets
 * without an instance id (pop-outs, previews) fall back to memory-only state.
 *
 * The store broadcasts tag signals; each instance decides whether to adopt
 * them based on its own mode, and defers feed-driven switches while its order
 * form is mid-entry.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import { AutoSwitchMode, useBrokerStore } from "@/stores/BrokerStore";

export const DEFAULT_SYMBOL = "EURUSD";

export const useBrokerWidgetSymbol = (widgetCompositeId?: string) => {
  const [activeSymbol, setActiveSymbol] = useStateWithOptionalPersistence<string>(
    widgetCompositeId ? `broker:symbol:${widgetCompositeId}` : null,
    DEFAULT_SYMBOL
  );
  const [autoSwitchMode, setAutoSwitchMode] =
    useStateWithOptionalPersistence<AutoSwitchMode>(
      widgetCompositeId ? `broker:auto-switch:${widgetCompositeId}` : null,
      "off"
    );

  const tagSignal = useBrokerStore((s) => s.tagSignal);
  const symbolForTags = useBrokerStore((s) => s.symbolForTags);

  // Set while the user is typing into this widget's order form — feed-driven
  // switches wait for blur so the asset never changes under a half-typed order.
  const [formBusy, setFormBusyState] = useState(false);
  const deferredRef = useRef<string | null>(null);

  const setFormBusy = useCallback(
    (busy: boolean) => {
      setFormBusyState(busy);
      if (!busy && deferredRef.current) {
        setActiveSymbol(deferredRef.current);
        deferredRef.current = null;
      }
    },
    [setActiveSymbol]
  );

  useEffect(() => {
    if (!tagSignal || autoSwitchMode === "off") return;
    if (tagSignal.channel === "feed" && autoSwitchMode !== "feed") return;

    const match = symbolForTags(tagSignal.tags);
    if (!match || match === activeSymbol) return;

    if (tagSignal.channel === "feed" && formBusy) {
      deferredRef.current = match;
      return;
    }
    setActiveSymbol(match);
    // Only react to new signals — activeSymbol/formBusy are read, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSignal]);

  return {
    activeSymbol,
    setActiveSymbol,
    autoSwitchMode,
    setAutoSwitchMode,
    setFormBusy,
  };
};
